/**
 * Mitglieder des aktiven Mandanten — Liste und Einladung (Spec 4 / 7.3).
 *
 * Die Einladung ist der letzte Schritt der Onboarding-Kette (Mandant → Queue →
 * Quelle → Mitglied) und lief bis hierher nur ueber `POST /members` ohne
 * Oberflaeche. Zwei Feinheiten:
 *
 *   - **Das Startpasswort gibt es genau einmal.** Legt die API fuer eine
 *     unbekannte Adresse ein Konto an, steht das Passwort nur in dieser einen
 *     Antwort (dieselbe Regel wie fuer API-Keys, Spec 11). Hat der Mensch
 *     schon ein Konto, behaelt er sein Passwort — dann gibt es nichts zu
 *     zeigen, und das UI sagt genau das.
 *   - **Rolle statt Scopes.** Die feine Einschraenkung auf einzelne Queues
 *     (`member_scopes`) bleibt der API vorbehalten; hier waehlt man die Rolle.
 *     Die Liste zeigt an, ob jemand eingeschraenkt ist.
 */
import { emailSchema, TENANT_ROLES, type TenantMemberDetail, type TenantRole } from '@tickethub/shared';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { SecretOnce } from '@/components/SourceSettings';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { toastError } from '@/lib/errors';
import { useSession } from '@/lib/session';
import { membersApi } from '@/lib/tickets';

export function MembersPage() {
  const { t } = useTranslation();
  const { user, activeMembership } = useSession();
  const [members, setMembers] = useState<TenantMemberDetail[] | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TenantRole>('agent');
  const [formError, setFormError] = useState<string | null>(null);
  /** Startpasswort eines frisch angelegten Kontos — nur bis zum Schliessen. */
  const [initialPassword, setInitialPassword] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const tenantId = activeMembership?.tenant_id;
  const mayManage = activeMembership?.role === 'admin' || user?.is_platform_admin === true;

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      setMembers((await membersApi.list(tenantId)).items);
    } catch (error) {
      setMembers([]);
      toastError(error, t);
    }
  }, [tenantId, t]);

  useEffect(() => {
    // `load` setzt State erst *nach* dem Netzwerk-Await — es entsteht keine
    // kaskadierende Render-Schleife. Die Regel kann das nicht unterscheiden.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Daten beim Mount laden
    void load();
  }, [load]);

  async function invite(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    if (!tenantId) return;

    const parsed = emailSchema.safeParse(email.trim());
    if (!parsed.success) {
      setFormError(t('members.invite_email_required'));
      return;
    }

    setBusy(true);
    try {
      const result = await membersApi.add(tenantId, { email: parsed.data, role, scopes: [] });
      setEmail('');
      toast.success(t('members.invited', { email: parsed.data }));
      if (result.initial_password === null) {
        toast.info(t('members.invite_existing', { email: parsed.data }));
      } else {
        setInitialPassword(result.initial_password);
      }
      await load();
    } catch (error) {
      toastError(error, t);
    } finally {
      setBusy(false);
    }
  }

  if (!activeMembership) {
    return (
      <Alert>
        <AlertDescription>{t('tenant.empty')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t('members.title')}</h1>

      {mayManage ? (
        <section className="space-y-3 rounded-lg border p-4">
          <h2 className="text-lg font-semibold">{t('members.invite_title')}</h2>
          <p className="text-sm text-muted-foreground">{t('members.invite_intro')}</p>

          {initialPassword ? (
            <SecretOnce
              title={t('members.password_once_title')}
              hint={t('members.password_once_hint')}
              value={initialPassword}
              onClose={() => setInitialPassword(null)}
            />
          ) : null}

          <form className="flex flex-wrap items-end gap-2" onSubmit={(event) => void invite(event)}>
            <div className="min-w-64 flex-1 space-y-2">
              <Label htmlFor="member-email">{t('members.invite_email')}</Label>
              <Input
                id="member-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="member-role">{t('tenant.role')}</Label>
              <Select
                id="member-role"
                value={role}
                onChange={(event) => setRole(event.target.value as TenantRole)}
              >
                {TENANT_ROLES.map((entry) => (
                  <option key={entry} value={entry}>
                    {t(`tenant.role_${entry}`)}
                  </option>
                ))}
              </Select>
            </div>
            <Button type="submit" disabled={busy}>
              {t('members.invite_submit')}
            </Button>
          </form>

          {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
        </section>
      ) : null}

      {members === null ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : members.length === 0 ? (
        <Alert>
          <AlertDescription>{t('members.empty')}</AlertDescription>
        </Alert>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">{t('members.name')}</th>
                <th className="px-4 py-2 font-medium">{t('members.email')}</th>
                <th className="px-4 py-2 font-medium">{t('tenant.role')}</th>
                <th className="px-4 py-2 font-medium">{t('members.scopes')}</th>
                <th className="px-4 py-2 font-medium">{t('members.two_factor')}</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.id} className="border-t">
                  <td className="px-4 py-2">{member.user.name}</td>
                  <td className="px-4 py-2">{member.user.email}</td>
                  <td className="px-4 py-2">{t(`tenant.role_${member.role}`)}</td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {member.scopes.length === 0 ? t('tenant.unscoped') : t('tenant.scoped')}
                  </td>
                  <td className="px-4 py-2">
                    {member.user.two_factor_enabled ? t('boolean.yes') : t('boolean.no')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
